// ===============================================================
// ⭐ ChangeEmailPage.js — Change Email (i18n + TopBar + LivoraToast)
// ===============================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ChangeEmailPage.css";

// 🔹 TopBar + Translation
import TopBar from "../components/common/TopBar";
import { useTranslation } from "react-i18next";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "../components/common/LivoraToast";

// 🔹 Firebase
import { auth, db } from "../firebase";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";

function ChangeEmailPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Livora Toast
  const { toast, showToast, hideToast } = useLivoraToast();

  const currentEmail =
    auth.currentUser?.email ||
    t("not_linked", { defaultValue: "غير مرتبط" });

  const handleChangeEmail = async (e) => {
    e.preventDefault();

    if (!newEmail || !password) {
      showToast(
        "error",
        t("change_email_fill_required", {
          defaultValue: "يرجى تعبئة جميع الحقول",
        })
      );
      return;
    }

    try {
      setLoading(true);

      const user = auth.currentUser;
      if (!user) {
        showToast(
          "error",
          t("general_error", {
            defaultValue: "حدث خطأ عام",
          })
        );
        return;
      }

      if (!user.email) {
        showToast(
          "error",
          t("email_not_available_for_reauth", {
            defaultValue:
              "لا يمكن إعادة التحقق لأن الحساب لا يحتوي على بريد إلكتروني",
          })
        );
        return;
      }

      const credential = EmailAuthProvider.credential(user.email, password);

      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail);

      await updateDoc(doc(db, "users", user.uid), {
        email: newEmail,
      });

      showToast(
        "success",
        t("change_email_success", {
          defaultValue: "تم تغيير البريد الإلكتروني بنجاح",
        })
      );

      setNewEmail("");
      setPassword("");

      setTimeout(() => {
        navigate("/settings");
      }, 500);
    } catch (error) {
      console.error(error);

      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        showToast(
          "error",
          t("wrong_password", {
            defaultValue: "كلمة المرور غير صحيحة",
          })
        );
      } else if (error.code === "auth/email-already-in-use") {
        showToast(
          "error",
          t("email_already_in_use", {
            defaultValue: "هذا البريد مستخدم بالفعل",
          })
        );
      } else if (error.code === "auth/invalid-email") {
        showToast(
          "error",
          t("invalid_email", {
            defaultValue: "البريد الإلكتروني غير صالح",
          })
        );
      } else if (error.code === "auth/requires-recent-login") {
        showToast(
          "error",
          t("requires_recent_login", {
            defaultValue: "يجب تسجيل الدخول مجددًا قبل تنفيذ هذا الإجراء",
          })
        );
      } else {
        showToast(
          "error",
          t("general_error", {
            defaultValue: "حدث خطأ عام",
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-email-page">
      <TopBar
        title={t("change_email", { defaultValue: "تغيير البريد الإلكتروني" })}
        onBack={() => navigate(-1)}
      />

      <div className="page-content">
        <form onSubmit={handleChangeEmail}>
          <div className="current-email-box">
            <div className="current-email-label">
              {t("current_email", {
                defaultValue: "البريد الإلكتروني الحالي",
              })}
            </div>
            <div className="current-email-value">{currentEmail}</div>
          </div>

          <input
            type="email"
            className="input-field"
            placeholder={t("new_email", {
              defaultValue: "البريد الإلكتروني الجديد",
            })}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
          />

          <input
            type="password"
            className="input-field"
            placeholder={t("current_password", {
              defaultValue: "كلمة المرور الحالية",
            })}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />

          <button
            className="main-btn"
            type="submit"
            disabled={loading}
          >
            {loading
              ? t("changing", { defaultValue: "جارٍ التغيير..." })
              : t("change_email_button", {
                  defaultValue: "تغيير البريد الإلكتروني",
                })}
          </button>
        </form>
      </div>

      {/* ✅ Livora Toast */}
      <LivoraToast toast={toast} onClose={hideToast} />
    </div>
  );
}

export default ChangeEmailPage;